
import React, { useState, useEffect } from 'react';
import { downloadQRCodePNG, downloadQRCodeSVG, generateQRCodeDataURL, QRGenerationOptions } from '../utils/qrGenerator';

interface QRCodeDisplayProps {
  qrId: string;
  size?: number;
  showDownloadButton?: boolean;
  className?: string;
  styleOptions?: QRGenerationOptions;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  qrId,
  size = 200,
  showDownloadButton = true,
  className = '',
  styleOptions
}) => {
  const [qrDataURL, setQrDataURL] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  useEffect(() => {
    const generateQR = async () => {
      try {
        setLoading(true);
        setError('');

        // Use responsive size based on screen width for display
        const displaySize = window.innerWidth < 640 ? 160 : size;

        // Generate at higher resolution for better quality (min 512px or 2x display size)
        // This ensures logos and QR modules look sharp even on high DPI screens
        const generationSize = Math.max(displaySize * 2, 512);

        // Provide better defaults for existing QR codes without styleOptions
        const defaultStyleOptions: QRGenerationOptions = {
          size: 256,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'M',
          logo: {
            enabled: false,
            size: 40,
            borderRadius: 8,
            backgroundColor: '#FFFFFF'
          },
          style: {
            transparentBackground: false,
            dotStyle: 'square',
            gradientColors: {
              enabled: false,
              startColor: '#000000',
              endColor: '#333333',
              direction: 'diagonal'
            }
          }
        };

        // Merge default options, then style options, then generation size
        const options: QRGenerationOptions = {
          ...defaultStyleOptions,
          ...styleOptions,
          size: generationSize, // Use higher resolution for generation
        };

        // Use targetUrl if available, otherwise use qrId (which will generate cloud function URL)
        const dataURL = await generateQRCodeDataURL(qrId, options);
        setQrDataURL(dataURL);
      } catch (err) {
        setError('Failed to generate QR code');
        console.error('QR generation error:', err);
      } finally {
        setLoading(false);
      }
    };

    generateQR();
  }, [qrId, size, styleOptions]);

  const handleDownload = async (format: 'png' | 'svg') => {
    try {
      const options: QRGenerationOptions = {
        size: styleOptions?.size || 512,
        ...styleOptions
      };
      if (format === 'svg') {
        await downloadQRCodeSVG(qrId, `qr-code-${qrId}.svg`, options);
      } else {
        await downloadQRCodePNG(qrId, `qr-code-${qrId}.png`, options);
      }
      setShowDownloadMenu(false);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (loading) {
    return (
      <div className={`flex items - center justify - center ${className} `} style={{ width: size, height: size }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items - center justify - center bg - red - 50 border border - red - 200 rounded - lg ${className} `} style={{ width: size, height: size }}>
        <div className="text-center p-4">
          <svg className="mx-auto h-8 w-8 text-red-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative group ${className} `}>
      <img
        src={qrDataURL}
        alt={`QR Code for ${qrId}`}
        className="rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
        style={{ width: size, height: size }}
      />
      {showDownloadButton && (
        <div className="absolute right-3 top-3">
          <button
            type="button"
            onClick={() => setShowDownloadMenu((current) => !current)}
            className="rounded-full bg-white/95 p-3 shadow-md transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 group-hover:opacity-100 lg:opacity-0"
            title="Download QR code"
            aria-label="Download QR code"
            aria-expanded={showDownloadMenu}
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0-4-4m4 4 4-4M5 20h14" /></svg>
          </button>
          {showDownloadMenu && (
            <div className="absolute right-0 top-14 z-20 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
              <button type="button" onClick={() => void handleDownload('png')} className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-100"><span>PNG image</span><span className="text-[10px] text-slate-400">.png</span></button>
              <button type="button" onClick={() => void handleDownload('svg')} className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-100"><span>Vector SVG</span><span className="text-[10px] text-slate-400">.svg</span></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QRCodeDisplay;
